import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faPlus, faMinus, faAlignLeft, faSearch } from '@fortawesome/free-solid-svg-icons';

// Decode a stored HTTP message body (handles base64-encoded bodies)
const decodeBody = (msg) => {
  if (!msg) return '';
  const body = msg.body || '';
  if (msg.bodyEncoding === 'base64') {
    try { return atob(body); } catch { return body; }
  }
  return body;
};

// Split text into diffable lines. Minified HTML/JSON (few newlines, long lines)
// is lightly pretty-printed so line-level diff can actually locate changes.
const toLines = (text) => {
  if (!text) return [];
  let t = text;
  const newlines = (t.match(/\n/g) || []).length;
  if (t.length > 2000 && newlines < t.length / 400) {
    t = t.replace(/>\s*</g, '>\n<');       // break between HTML tags
    if ((t.match(/\n/g) || []).length < t.length / 400) {
      t = t.replace(/([}\],])(?=["{[])/g, '$1\n'); // break minified JSON
    }
  }
  return t.split('\n');
};

// Coarse index-aligned diff — fallback when the LCS matrix would be too large
const coarseDiff = (a, b) => {
  const rows = [];
  const max = Math.max(a.length, b.length);
  for (let k = 0; k < max; k++) {
    const left = k < a.length ? a[k] : null;
    const right = k < b.length ? b[k] : null;
    if (left === right) rows.push({ type: 'equal', left, right, li: k, ri: k });
    else rows.push({ type: 'change', left, right, li: left != null ? k : null, ri: right != null ? k : null });
  }
  return rows;
};

// Line-level LCS diff producing aligned rows (equal / del / add)
const diffLines = (a, b) => {
  const n = a.length, m = b.length;
  if (n === 0 && m === 0) return [];
  if (n * m > 4_000_000) return coarseDiff(a, b); // ~2000×2000 cap

  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const rows = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { rows.push({ type: 'equal', left: a[i], right: b[j], li: i, ri: j }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { rows.push({ type: 'del', left: a[i], right: null, li: i, ri: null }); i++; }
    else { rows.push({ type: 'add', left: null, right: b[j], li: null, ri: j }); j++; }
  }
  while (i < n) { rows.push({ type: 'del', left: a[i], right: null, li: i, ri: null }); i++; }
  while (j < m) { rows.push({ type: 'add', left: null, right: b[j], li: null, ri: j }); j++; }
  return rows;
};

/**
 * ResponseDiffView — side-by-side, synced-scrolling line diff of the baseline
 * vs test message. Because both columns live in ONE scroll container, scrolling
 * is inherently synced and the two sides stay aligned line-for-line.
 */
const ResponseDiffView = ({ baseline, test, testLabel = 'Test', baselineLabel = 'Baseline (High Privilege)', isDarkMode }) => {
  const [part, setPart] = useState('response'); // 'response' | 'request'
  const containerRef = useRef(null);
  const [diffCursor, setDiffCursor] = useState(-1);
  const [query, setQuery] = useState('');
  const [matchCursor, setMatchCursor] = useState(0);

  const { rows, changedIdx, stats } = useMemo(() => {
    const baseMsg = part === 'response' ? baseline?.response : baseline?.request;
    const testMsg = part === 'response' ? test?.response : test?.request;
    const aLines = toLines(decodeBody(baseMsg));
    const bLines = toLines(decodeBody(testMsg));
    const diff = diffLines(aLines, bLines);
    const changed = [];
    let added = 0, removed = 0;
    diff.forEach((r, idx) => {
      if (r.type !== 'equal') {
        changed.push(idx);
        if (r.type === 'add') added++;
        else if (r.type === 'del') removed++;
        else { added++; removed++; }
      }
    });
    return { rows: diff, changedIdx: changed, stats: { added, removed } };
  }, [baseline, test, part]);

  // Find-in-diff: rows whose baseline or test text contains the query.
  const matchIdx = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const out = [];
    rows.forEach((r, i) => {
      if ((r.left && r.left.toLowerCase().includes(q)) || (r.right && r.right.toLowerCase().includes(q))) out.push(i);
    });
    return out;
  }, [rows, query]);

  useEffect(() => {
    setMatchCursor(0);
    if (matchIdx.length) {
      const el = containerRef.current?.querySelector(`[data-row="${matchIdx[0]}"]`);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const jumpToMatch = (dir) => {
    if (matchIdx.length === 0) return;
    const c = dir === 'next' ? (matchCursor + 1) % matchIdx.length : (matchCursor - 1 + matchIdx.length) % matchIdx.length;
    setMatchCursor(c);
    const el = containerRef.current?.querySelector(`[data-row="${matchIdx[c]}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // Render a line, highlighting occurrences of the query.
  const hl = (text) => {
    if (!text || !query.trim()) return text;
    const q = query, ql = q.toLowerCase(), lower = text.toLowerCase();
    const parts = []; let i = 0, start = 0;
    while ((i = lower.indexOf(ql, start)) !== -1) {
      if (i > start) parts.push(text.slice(start, i));
      parts.push(<mark key={parts.length} className="bg-yellow-300 text-black rounded-sm">{text.slice(i, i + q.length)}</mark>);
      start = i + q.length;
    }
    if (start < text.length) parts.push(text.slice(start));
    return parts;
  };

  const jumpToDiff = (dir) => {
    if (changedIdx.length === 0) return;
    let next;
    if (dir === 'next') {
      next = changedIdx.find(idx => idx > diffCursor);
      if (next === undefined) next = changedIdx[0];
    } else {
      const prior = [...changedIdx].reverse().find(idx => idx < diffCursor);
      next = prior !== undefined ? prior : changedIdx[changedIdx.length - 1];
    }
    setDiffCursor(next);
    const el = containerRef.current?.querySelector(`[data-row="${next}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const rowBg = (type, side) => {
    if (type === 'equal') return 'transparent';
    if (type === 'del') return side === 'left'
      ? (isDarkMode ? 'bg-red-900/30' : 'bg-red-50')
      : (isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50');
    if (type === 'add') return side === 'right'
      ? (isDarkMode ? 'bg-green-900/30' : 'bg-green-50')
      : (isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50');
    // change
    return side === 'left'
      ? (isDarkMode ? 'bg-red-900/25' : 'bg-red-50')
      : (isDarkMode ? 'bg-green-900/25' : 'bg-green-50');
  };

  const gutter = isDarkMode ? 'text-gray-600' : 'text-gray-400';
  const codeText = isDarkMode ? 'text-gray-300' : 'text-gray-800';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Diff toolbar */}
      <div className={`flex items-center justify-between px-3 py-1.5 border-b text-xs ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faAlignLeft} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Body diff</span>
          <div className="flex rounded overflow-hidden border border-gray-500/30">
            {['response', 'request'].map(p => (
              <button
                key={p}
                onClick={() => { setPart(p); setDiffCursor(-1); }}
                className={`px-2 py-0.5 capitalize ${
                  part === p ? 'bg-amber-600 text-white' : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Find in diff */}
          <div className={`flex items-center gap-1 rounded border px-1.5 ${isDarkMode ? 'border-gray-600 bg-gray-900' : 'border-gray-300 bg-white'}`}>
            <FontAwesomeIcon icon={faSearch} className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') jumpToMatch(e.shiftKey ? 'prev' : 'next'); }}
              placeholder="Find in diff…"
              className={`w-32 py-0.5 bg-transparent outline-none text-[11px] ${isDarkMode ? 'text-gray-200 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
            />
            {query && (
              <>
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {matchIdx.length ? `${matchCursor + 1}/${matchIdx.length}` : '0'}
                </span>
                <button onClick={() => jumpToMatch('prev')} disabled={!matchIdx.length} className={`px-1 rounded disabled:opacity-40 ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`} title="Previous match (Shift+Enter)">
                  <FontAwesomeIcon icon={faChevronUp} className="text-[9px]" />
                </button>
                <button onClick={() => jumpToMatch('next')} disabled={!matchIdx.length} className={`px-1 rounded disabled:opacity-40 ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`} title="Next match (Enter)">
                  <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" />
                </button>
              </>
            )}
          </div>
          <span className="flex items-center gap-1 text-green-500"><FontAwesomeIcon icon={faPlus} className="text-[9px]" />{stats.added}</span>
          <span className="flex items-center gap-1 text-red-500"><FontAwesomeIcon icon={faMinus} className="text-[9px]" />{stats.removed}</span>
          {changedIdx.length > 0 ? (
            <div className="flex items-center gap-1">
              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>{changedIdx.length} changes</span>
              <button onClick={() => jumpToDiff('prev')} className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`} title="Previous change">
                <FontAwesomeIcon icon={faChevronUp} className="text-[9px]" />
              </button>
              <button onClick={() => jumpToDiff('next')} className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`} title="Next change">
                <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" />
              </button>
            </div>
          ) : (
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>No differences</span>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className={`grid grid-cols-2 text-[11px] font-semibold border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className={`px-3 py-1 border-r truncate ${isDarkMode ? 'border-gray-700 text-blue-400' : 'border-gray-200 text-blue-600'}`}>{baselineLabel}</div>
        <div className={`px-3 py-1 truncate ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{testLabel}</div>
      </div>

      {/* Synced diff body (single scroll container -> both sides scroll together) */}
      <div ref={containerRef} className="flex-1 overflow-auto font-mono text-[11px] leading-relaxed">
        {rows.length === 0 ? (
          <div className={`p-4 text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Empty body</div>
        ) : (
          rows.map((r, idx) => {
            const isCursor = idx === diffCursor;
            const isMatch = matchIdx.length > 0 && idx === matchIdx[matchCursor];
            return (
              <div
                key={idx}
                data-row={idx}
                className={`grid grid-cols-2 ${isMatch ? (isDarkMode ? 'ring-1 ring-yellow-400' : 'ring-1 ring-yellow-500') : isCursor ? (isDarkMode ? 'ring-1 ring-amber-500' : 'ring-1 ring-amber-400') : ''}`}
              >
                {/* Left / baseline */}
                <div className={`flex border-r ${isDarkMode ? 'border-gray-800' : 'border-gray-100'} ${rowBg(r.type, 'left')}`}>
                  <span className={`select-none w-10 flex-shrink-0 text-right pr-2 ${gutter}`}>{r.li != null ? r.li + 1 : ''}</span>
                  <span className={`whitespace-pre-wrap break-all pr-2 ${codeText}`}>{hl(r.left)}</span>
                </div>
                {/* Right / test */}
                <div className={`flex ${rowBg(r.type, 'right')}`}>
                  <span className={`select-none w-10 flex-shrink-0 text-right pr-2 ${gutter}`}>{r.ri != null ? r.ri + 1 : ''}</span>
                  <span className={`whitespace-pre-wrap break-all pr-2 ${codeText}`}>{hl(r.right)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ResponseDiffView;
